/**
 * Over/under — what a trade was written for versus what it's actually worth.
 *
 *   overUnder = tradeAllowance − ACV
 *
 * Giving a customer more for their trade than its actual cash value is a real
 * cost of moving the unit: the over-allowance rides along on the bike being
 * sold. It's signed and symmetric — an over adds cost and lowers gross, an
 * under subtracts cost and raises gross — so the average across a month is a
 * true mix rather than a one-sided tally.
 *
 * Like the doc fee, this is applied at REPORT time and never folded into the
 * stored `totalCost`. Total cost stays exactly what the person entering the
 * deal typed, so the entry is auditable and the adjustment stays reversible;
 * gross is computed as `salesPrice − totalCost − overUnder`.
 *
 * Only deals with an actual trade participate. A deal with no trade returns
 * null — it contributes no cost and is excluded from the average's denominator
 * rather than diluting it with zeros.
 *
 * Mirrors backend/src/utils/overUnder.ts. The scoreboards aggregate activities
 * client-side while Profit Summary aggregates server-side, so the two
 * implementations must stay in step.
 */

const round2 = (n) => Math.round(n * 100) / 100;

/** A row only counts if it's an actual bike deal; non-unit rows are excluded. */
export function isBikeDeal(activity) {
  const val = String((activity && activity.motorcycleNewUsed) || '').trim().toUpperCase();
  return val === 'NEW' || val === 'USED';
}

/**
 * Signed over/under for one deal, or null when it doesn't apply — no trade, no
 * usable ACV, or not a bike deal. Null is deliberately distinct from 0: zero is
 * a trade written exactly at book, null is "this deal has nothing to say."
 */
export function overUnderFor(activity) {
  if (!isBikeDeal(activity)) return null;

  const allowance = Number(activity && activity.tradeAllowance);
  if (!isFinite(allowance) || allowance <= 0) return null;

  const acv = Number(activity && activity.acv);
  if (!isFinite(acv) || acv <= 0) return null;

  return round2(allowance - acv);
}

/** True when this deal carries a trade we can measure. */
export function hasTrade(activity) {
  return overUnderFor(activity) !== null;
}

/**
 * Total cost adjustment for a set of deals — the signed sum of their
 * over/unders. Add this to cost (equivalently, subtract it from gross).
 */
export function overUnderCost(activities) {
  return (activities || []).reduce((sum, a) => sum + (overUnderFor(a) || 0), 0);
}

/** How many of these deals carry a trade. The average's denominator. */
export function tradeCount(activities) {
  return (activities || []).filter(hasTrade).length;
}

/**
 * Average over/under per deal that had a trade. Null when none did, so callers
 * can render "—" instead of a misleading $0.
 */
export function avgOverUnder(activities) {
  const withTrade = (activities || []).filter(hasTrade);
  if (withTrade.length === 0) return null;
  const total = withTrade.reduce((sum, a) => sum + (overUnderFor(a) || 0), 0);
  return total / withTrade.length;
}
